import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  MessageSquare, 
  Send, 
  X, 
  Bot, 
  User, 
  Loader2,
  Sparkles,
  Brain
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFloatingChat } from '../contexts/FloatingChatContext';
import { whatsappAPI } from '../services/api';
import { toast } from 'react-toastify';
import './FloatingChatSystem.css';

const FloatingChatSystem = () => {
  const { user } = useAuth();
  const { settings, shouldShowButtons, getButtonPosition } = useFloatingChat();
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [whatsappChatOpen, setWhatsappChatOpen] = useState(false);

  const [aiMessages, setAiMessages] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [aiInputMessage, setAiInputMessage] = useState('');
  const [whatsappInputMessage, setWhatsappInputMessage] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [aiConversationId, setAiConversationId] = useState(null);
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState('');
  const [whatsappUserName, setWhatsappUserName] = useState('');
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [whatsappOtpSent, setWhatsappOtpSent] = useState(false);
  const [whatsappOtpCode, setWhatsappOtpCode] = useState('');
  const [whatsappOtpVerifying, setWhatsappOtpVerifying] = useState(false);
  const [whatsappOtpResending, setWhatsappOtpResending] = useState(false);
  const [whatsappOtpVerified, setWhatsappOtpVerified] = useState(false);
  const [whatsappErrorMessage, setWhatsappErrorMessage] = useState('');
  const [whatsappErrorShake, setWhatsappErrorShake] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [replyCheckInterval, setReplyCheckInterval] = useState(null);
  
  const aiMessagesEndRef = useRef(null);
  const whatsappMessagesEndRef = useRef(null);
  const aiInputRef = useRef(null);
  const whatsappInputRef = useRef(null);

  // Determine which chat is on top based on which one is open
  const aiChatOnTop = aiChatOpen && !whatsappChatOpen;
  const whatsappChatOnTop = whatsappChatOpen && !aiChatOpen;

  // Helper functions for WhatsApp validation and error handling
  const isValidPhoneWithCountryCode = (phone) => {
    // E.164 format: +[country code][number], total length 8-15 digits after +
    return /^\+[1-9]\d{7,14}$/.test((phone || '').trim());
  };

  const showWhatsappError = (message) => {
    if (whatsappErrorMessage === message) {
      // Same error - trigger shake animation
      setWhatsappErrorShake(true);
      setTimeout(() => setWhatsappErrorShake(false), 500);
    } else {
      // New error message
      setWhatsappErrorMessage(message);
      setWhatsappErrorShake(false);
    }
  };

  const clearWhatsappError = () => {
    setWhatsappErrorMessage('');
    setWhatsappErrorShake(false);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  const scrollToBottom = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom(aiMessagesEndRef);
  }, [aiMessages]);

  useEffect(() => {
    scrollToBottom(whatsappMessagesEndRef);
  }, [whatsappMessages]);

  useEffect(() => {
    if (aiChatOpen && aiInputRef.current) {
      aiInputRef.current.focus();
    }
  }, [aiChatOpen]);

  useEffect(() => {
    if (whatsappChatOpen && whatsappInputRef.current) {
      whatsappInputRef.current.focus();
    }
  }, [whatsappChatOpen]);

  // Close other chat when one is opened
  const handleAiChatToggle = () => {
    if (whatsappChatOpen) {
      setWhatsappChatOpen(false);
    }
    setAiChatOpen(!aiChatOpen);
  };

  const handleWhatsappChatToggle = () => {
    if (aiChatOpen) {
      setAiChatOpen(false);
    }
    
    if (!whatsappChatOpen) {
      // Opening chat - reset states for fresh start
      setWhatsappPhoneNumber('');
      setWhatsappUserName('');
      setWhatsappOtpCode('');
      setWhatsappOtpSent(false);
      setWhatsappOtpVerified(false);
      setWhatsappConnected(false);
      setWhatsappMessages([]);
      clearWhatsappError();
    }
    
    setWhatsappChatOpen(!whatsappChatOpen);
  };

  // AI Chat Functions
  const sendAiMessage = async () => {
    if (!aiInputMessage.trim() || aiLoading) return;

    const userMessage = {
      id: Date.now(),
      text: aiInputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setAiMessages(prev => [...prev, userMessage]);
    setAiInputMessage('');
    setAiLoading(true);

    try {
      console.log('🤖 Sending AI message:', aiInputMessage);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: aiInputMessage,
          conversation_id: aiConversationId,
          user_id: user?.id
        })
      });

      console.log('🤖 AI API response status:', response.status);

      const data = await response.json();
      console.log('🤖 AI API response data:', data);

      if (data.success) {
        const aiMessage = {
          id: Date.now() + 1,
          text: data.data.response,
          sender: 'ai',
          timestamp: new Date()
        };

        setAiMessages(prev => [...prev, aiMessage]);
        setAiConversationId(data.data.conversation_id);
      } else {
        console.error('❌ AI API error:', data.message);
        console.error('❌ AI API error details:', data.error);
        const errorMessage = {
          id: Date.now() + 1,
          text: data.error ? `Error: ${data.error}` : (data.message || 'Sorry, I encountered an error. Please try again.'),
          sender: 'ai',
          timestamp: new Date(),
          isError: true
        };
        setAiMessages(prev => [...prev, errorMessage]);
        
        // Show user-friendly toast for specific errors
        if (data.message === 'AI chat is currently disabled') {
          toast.error('AI chat is currently disabled. Please contact the administrator.');
        } else if (data.message === 'OpenAI is not configured') {
          toast.error('AI service is not configured. Please contact the administrator.');
        }
      }
    } catch (error) {
      console.error('❌ Error sending AI message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered a connection error. Please try again.',
        sender: 'ai',
        timestamp: new Date(),
        isError: true
      };
      setAiMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to connect to AI service. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendAiMessage();
    }
  };

  // WhatsApp Chat Functions
  const sendWhatsappMessage = async () => {
    if (!whatsappInputMessage.trim() || !whatsappPhoneNumber) {
      toast.error('Please enter both phone number and message');
      return;
    }

    const newMessage = {
      id: Date.now(),
      type: 'sent',
      text: whatsappInputMessage,
      timestamp: new Date(),
      sender: 'user'
    };

    setWhatsappMessages(prev => [...prev, newMessage]);
    setWhatsappLoading(true);

    try {
      // Log the message to the admin panel without sending via WhatsApp
      // This makes it appear in the admin's WhatsApp chat interface
      const response = await whatsappAPI.logWebsiteMessage({
        phoneNumber: whatsappPhoneNumber,
        message: whatsappInputMessage,
        userId: user?.id || null
      });

      if (response.data.success) {
        // Add success message
        const successMessage = {
          id: Date.now() + 1,
          type: 'system',
          text: 'Message sent to admin. You will receive replies here.',
          timestamp: new Date(),
          sender: 'system'
        };
        setWhatsappMessages(prev => [...prev, successMessage]);
        setWhatsappInputMessage('');
        setWhatsappLoading(false);
        
        // Start listening for replies
        startListeningForReplies(whatsappPhoneNumber);
      } else {
        toast.error('Failed to send message');
        setWhatsappLoading(false);
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send message';
      toast.error(errorMessage);
      setWhatsappLoading(false);
    }
  };

  // Function to listen for replies from business owner
  const startListeningForReplies = (customerPhone) => {
    // This will be implemented with WebSocket or polling
    console.log('Listening for replies from business owner for:', customerPhone);
    
    // For now, we'll use polling to check for new messages
    const checkForReplies = async () => {
      try {
        const response = await whatsappAPI.getConversationMessages(customerPhone);
        if (response.data.success && response.data.messages) {
          // Update messages with new replies
          setWhatsappMessages(prev => {
            const newMessages = response.data.messages.filter(msg => 
              !prev.find(existing => existing.id === msg.id)
            );
            return [...prev, ...newMessages];
          });
        }
      } catch (error) {
        console.error('Error checking for replies:', error);
      }
    };

    // Check every 5 seconds for new replies
    const interval = setInterval(checkForReplies, 5000);
    
    // Store interval ID to clear it later
    setReplyCheckInterval(interval);
  };

  const handleWhatsappKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendWhatsappMessage();
    }
  };

  const handleStartWhatsappChat = async () => {
    // Clear any previous errors when user tries again
    clearWhatsappError();

    // Check if we're in direct chat mode
    if (settings?.whatsapp_chat_mode === 'direct_chat') {
      if (!whatsappUserName.trim()) {
        showWhatsappError('Please enter your full name');
        return;
      }
      
      // Open WhatsApp directly with the message
      const message = (settings.whatsapp_direct_message_template || 'Hi {name}').replace('{name}', whatsappUserName.trim());
      const whatsappNumber = settings.whatsapp_direct_number || '+919515377622';
      const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(message)}`;
      
      window.open(whatsappUrl, '_blank');
      
      // Close the chat window
      setWhatsappChatOpen(false);
      return;
    }

    // Web Chat Mode - Original OTP flow
    if (!whatsappPhoneNumber.trim()) {
      showWhatsappError('Please enter your phone number');
      return;
    }
    
    if (!isValidPhoneWithCountryCode(whatsappPhoneNumber)) {
      showWhatsappError('Please enter the phone number with the country code.');
      return;
    }
    
    try {
      setWhatsappLoading(true);
      
      // First, validate if this is a WhatsApp number
      console.log('🔍 Validating WhatsApp number:', whatsappPhoneNumber);
      try {
        const validationRes = await whatsappAPI.validateNumber({
          phoneNumber: whatsappPhoneNumber
        });
        
        if (!validationRes.data?.success) {
          // Number is not on WhatsApp
          showWhatsappError('This number is not registered on WhatsApp.');
          toast.error('This number is not registered on WhatsApp.');
          return;
        }
        
        console.log('✅ WhatsApp number validation passed');
        
        // Check if validation was inconclusive
        if (validationRes.data?.message?.includes('inconclusive')) {
          console.log('⚠️ Validation inconclusive, will monitor during OTP sending');
        }
      } catch (validationError) {
        console.error('❌ WhatsApp validation failed:', validationError);
        const errorMsg = validationError.response?.data?.error || validationError.message || '';
        
        if (errorMsg.includes('not registered') || errorMsg.includes('WhatsApp')) {
          showWhatsappError('This number is not registered on WhatsApp.');
          toast.error('This number is not registered on WhatsApp.');
        } else if (errorMsg.includes('Invalid phone number format')) {
          showWhatsappError('Please enter a valid phone number format.');
          toast.error('Please enter a valid phone number format.');
        } else {
          showWhatsappError('Unable to validate number. Please try again.');
          toast.error('Unable to validate number. Please try again.');
        }
        return;
      }
      
      // If validation passed, proceed with OTP
      console.log('📤 Sending OTP to validated WhatsApp number');
      const res = await whatsappAPI.sendOtp({
        phoneNumber: whatsappPhoneNumber,
        verificationType: 'chat'
      });
      
      if (res.data?.success) {
        setWhatsappOtpSent(true);
        setWhatsappMessages([
          {
            id: Date.now(),
            type: 'system',
            text: 'Please submit the OTP to start chatting.',
            timestamp: new Date(),
            sender: 'system'
          }
        ]);
        clearWhatsappError(); // Clear any errors on success
        toast.success('OTP sent via WhatsApp');
        
        // Start monitoring for delivery failures
        startDeliveryFailureMonitoring();
      } else {
        // Handle specific error cases
        const errorMsg = res.data?.error || 'Failed to send OTP';
        if (errorMsg.includes('not registered') || errorMsg.includes('WhatsApp')) {
          showWhatsappError('This number is not registered on WhatsApp.');
          toast.error('This number is not registered on WhatsApp.');
        } else {
          showWhatsappError('Failed to send OTP. Please try again.');
          toast.error('Failed to send OTP. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error in WhatsApp chat process:', error);
      const errorMsg = error.response?.data?.error || error.message || '';
      
      if (errorMsg.includes('not registered') || errorMsg.includes('WhatsApp')) {
        showWhatsappError('This number is not registered on WhatsApp.');
        toast.error('This number is not registered on WhatsApp.');
      } else if (errorMsg.includes('invalid') || errorMsg.includes('format')) {
        showWhatsappError('Please enter the phone number with the country code.');
        toast.error('Please enter the phone number with the country code.');
      } else {
        showWhatsappError('Failed to process request. Please try again.');
        toast.error('Failed to process request. Please try again.');
      }
    } finally {
      setWhatsappLoading(false);
    }
  };
  
  // Monitor for delivery failures after OTP is sent
  const startDeliveryFailureMonitoring = () => {
    // Check for delivery failures every 2 seconds for up to 10 seconds
    let attempts = 0;
    const maxAttempts = 5;
    
    const checkFailure = async () => {
      attempts++;
      
      try {
        // Try to verify with dummy OTP to trigger failure check
        await whatsappAPI.verifyOtp({
          phoneNumber: whatsappPhoneNumber,
          otp: '000000', // Dummy OTP
          verificationType: 'chat'
        });
      } catch (checkError) {
        const errorMsg = checkError.response?.data?.error || '';
        if (errorMsg.includes('invalid WhatsApp number') || 
            errorMsg.includes('not registered on WhatsApp')) {
          // WhatsApp delivery failed - invalid number
          setWhatsappOtpSent(false);
          setWhatsappMessages([]);
          showWhatsappError('This number is not registered on WhatsApp.');
          toast.error('This number is not registered on WhatsApp.');
          return; // Stop checking
        }
      }
      
      // Continue checking if we haven't reached max attempts
      if (attempts < maxAttempts) {
        setTimeout(checkFailure, 2000);
      }
    };
    
    // Start checking after 2 seconds
    setTimeout(checkFailure, 2000);
  };

  const handleVerifyWhatsappOtp = async () => {
    // Clear any previous errors when user tries again
    clearWhatsappError();

    if (!whatsappOtpCode.trim()) {
      showWhatsappError('Please enter the OTP');
      return;
    }
    
    // Validate OTP is 6 digits
    if (!/^\d{6}$/.test(whatsappOtpCode.trim())) {
      showWhatsappError('Wrong OTP. Please enter a 6-digit number.');
      return;
    }
    
    try {
      setWhatsappOtpVerifying(true);
      const res = await whatsappAPI.verifyOtp({
        phoneNumber: whatsappPhoneNumber,
        otp: whatsappOtpCode,
        verificationType: 'chat'
      });
      
      if (res.data?.success) {
        setWhatsappOtpVerified(true);
    setWhatsappConnected(true);
        setWhatsappOtpSent(false); // Hide OTP input section
        setWhatsappOtpCode(''); // Clear OTP input field
        // Clear the OTP prompt message and add welcome message
    setWhatsappMessages([
      {
            id: Date.now() + 1,
        type: 'received',
        text: 'Hello! Welcome to our WhatsApp support. How can we help you today?',
        timestamp: new Date(),
        sender: 'support'
      }
    ]);
        clearWhatsappError(); // Clear any errors on success
        toast.success('OTP verified. You can start chatting.');
      } else {
        showWhatsappError('Wrong OTP. Please check and try again.');
      }
    } catch (error) {
      console.error('Error verifying WhatsApp OTP:', error);
      const errorMsg = error.response?.data?.error || error.message || '';
      
      if (errorMsg.includes('invalid') || errorMsg.includes('expired') || errorMsg.includes('wrong')) {
        showWhatsappError('Wrong OTP. Please check and try again.');
      } else {
        showWhatsappError('Failed to verify OTP. Please try again.');
      }
    } finally {
      setWhatsappOtpVerifying(false);
    }
  };

  const handleResendWhatsappOtp = async () => {
    try {
      setWhatsappOtpResending(true);
      const res = await whatsappAPI.sendOtp({
        phoneNumber: whatsappPhoneNumber,
        verificationType: 'chat'
      });
      if (res.data?.success) {
        toast.success('OTP resent');
      } else {
        toast.error(res.data?.error || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Error resending WhatsApp OTP:', error);
      toast.error(error.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setWhatsappOtpResending(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Helper function to format AI responses with proper formatting
  const formatAiResponse = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>')
      .replace(/^(\d+\.\s)/gm, '<br>$1')
      .replace(/^(-\s)/gm, '<br>• ')
      .replace(/^(\*\s)/gm, '<br>• ');
  };

  // Don't render anything on server side or if buttons should not be shown
  if (!isClient || !shouldShowButtons()) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Buttons */}
      {createPortal(
        <div className="floating-chat-buttons" style={getButtonPosition()}>
          {/* WhatsApp Chat Button - Only show if enabled */}
          {settings.whatsapp_enabled && (
          <button
            onClick={handleWhatsappChatToggle}
            className={`floating-chat-button whatsapp ${whatsappChatOnTop ? 'on-top' : 'on-bottom'}`}
            style={{
                zIndex: whatsappChatOnTop ? 52 : 51,
                backgroundColor: settings.whatsapp_button_color
            }}
              aria-label={settings.whatsapp_button_text}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
          </button>
          )}

          {/* AI Chat Button - Only show if enabled */}
          {settings.ai_chat_enabled && (
          <button
            onClick={handleAiChatToggle}
            className={`floating-chat-button ai ${aiChatOnTop ? 'on-top' : 'on-bottom'}`}
            style={{
                zIndex: aiChatOnTop ? 52 : 51,
                backgroundColor: settings.ai_button_color
            }}
              aria-label={settings.ai_button_text}
          >
            <Brain className="w-6 h-6" />
          </button>
          )}
        </div>,
        document.body
      )}

      {/* AI Chat Window */}
      {aiChatOpen && createPortal(
        <div 
          className={`chat-window ai-chat ${aiChatOnTop ? 'ai-chat' : ''}`}
          style={{
            zIndex: aiChatOnTop ? 53 : 52,
            bottom: aiChatOnTop ? '9rem' : '8.5rem'
          }}
        >
          {/* AI Chat Header */}
          <div className="chat-header bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mr-4 border border-white/30">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">AI Assistant</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full online-pulse"></div>
                  <p className="text-sm opacity-90">Powered by OpenAI</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setAiChatOpen(false)}
                className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white/10 transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* AI Chat Body */}
          {(
            <>
              <div className="chat-body">
                {aiMessages.length === 0 ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Brain className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      AI Assistant
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-base max-w-xs mx-auto">
                      Ask me anything! I'm here to help with your questions and provide assistance.
                    </p>
                    <div className="mt-4 space-y-2">
                      <button
                        onClick={() => setAiInputMessage("Tell me about your services")}
                        className="text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        Services
                      </button>
                      <button
                        onClick={() => setAiInputMessage("What are your pricing plans?")}
                        className="text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-full hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors ml-2"
                      >
                        Pricing
                      </button>
                      <button
                        onClick={() => setAiInputMessage("How can I contact you?")}
                        className="text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-full hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors ml-2"
                      >
                        Contact
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aiMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`message ${message.sender === 'user' ? 'user' : 'ai'}`}
                      >
                        <div className="message-bubble">
                          <div className="flex items-start space-x-3">
                            {message.sender === 'ai' && (
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <Brain className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <div className="flex-1">
                          <div 
                            className="text-sm leading-relaxed whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{
                              __html: message.sender === 'ai' ? formatAiResponse(message.text) : message.text
                            }}
                          />
                              <div className="message-time">
                                {new Date(message.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                            {message.sender === 'user' && (
                              <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {aiLoading && (
                      <div className="message ai">
                        <div className="typing-indicator">
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div ref={aiMessagesEndRef} />
              </div>

              {/* AI Chat Input */}
              <div className="chat-input">
                <div className="flex items-center space-x-3">
                  <input
                    ref={aiInputRef}
                    type="text"
                    value={aiInputMessage}
                    onChange={(e) => setAiInputMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendAiMessage();
                      }
                    }}
                    placeholder="Type your message..."
                    className="flex-1"
                    disabled={aiLoading}
                  />
                  <button
                    onClick={sendAiMessage}
                    disabled={aiLoading || !aiInputMessage.trim()}
                    className="send-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aiLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>,
        document.body
      )}

      {/* WhatsApp Chat Window */}
      {whatsappChatOpen && createPortal(
        <div 
          className={`chat-window whatsapp-chat ${whatsappChatOnTop ? 'whatsapp-chat' : ''}`}
          style={{
            zIndex: whatsappChatOnTop ? 53 : 52,
            bottom: whatsappChatOnTop ? '9rem' : '8.5rem'
          }}
        >
          {/* WhatsApp Chat Header */}
          <div className="chat-header bg-gradient-to-r from-green-500 to-emerald-600 text-white">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mr-4 border border-white/30">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-lg">WhatsApp Support</h3>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${whatsappConnected ? 'bg-green-400 online-pulse' : 'bg-gray-400'}`}></div>
                  <p className="text-sm opacity-90">
                  {whatsappConnected ? 'Online' : 'Click to start chat'}
                </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setWhatsappChatOpen(false);
                  // Reset all WhatsApp states when closing
                  setWhatsappPhoneNumber('');
                  setWhatsappOtpCode('');
                  setWhatsappOtpSent(false);
                  setWhatsappOtpVerified(false);
                  setWhatsappConnected(false);
                  setWhatsappMessages([]);
                  clearWhatsappError();
                }}
                className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white/10 transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* WhatsApp Chat Body */}
          {(
            <>
              <div className="chat-body">
                {!whatsappConnected ? (
                  <div className="text-center py-5">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      WhatsApp Support
                      </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-base max-w-xs mx-auto mb-4">
                      Connect with us on WhatsApp for instant support and assistance.
                    </p>
                    
                    {/* Error Message Display */}
                    {whatsappErrorMessage && (
                      <div 
                        className={`max-w-xs mx-auto mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm font-medium transition-all duration-300 ${
                          whatsappErrorShake ? 'animate-shake' : ''
                        }`}
                        style={{
                          animation: whatsappErrorShake ? 'shake 0.5s ease-in-out' : undefined
                        }}
                      >
                        {whatsappErrorMessage}
                      </div>
                    )}

                    {/* Phone Number Input or OTP Input */}
                    {!whatsappOtpSent ? (
                      <>
                        {/* Direct Chat Mode - Name Input */}
                        {settings?.whatsapp_chat_mode === 'direct_chat' ? (
                          <div className="mb-4 max-w-xs mx-auto">
                            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-300 mb-2">
                              Your Full Name *
                            </label>
                            <input
                              type="text"
                              value={whatsappUserName}
                              onChange={(e) => {
                                setWhatsappUserName(e.target.value);
                                clearWhatsappError(); // Clear error when user starts typing
                              }}
                              placeholder="Enter your full name"
                              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/95 dark:bg-gray-800/80 backdrop-blur-md transition-all duration-200 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                        ) : (
                          /* Web Chat Mode - Phone Number Input */
                    <div className="mb-4 max-w-xs mx-auto">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-300 mb-2">
                        Your Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={whatsappPhoneNumber}
                              onChange={(e) => {
                                setWhatsappPhoneNumber(e.target.value);
                                clearWhatsappError(); // Clear error when user starts typing
                              }}
                              placeholder="Enter your phone number (e.g. +91XXXXXXXXXX)"
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/95 dark:bg-gray-800/80 backdrop-blur-md transition-all duration-200 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                        )}
                      <button
                        onClick={handleStartWhatsappChat}
                        disabled={whatsappLoading || (
                          settings?.whatsapp_chat_mode === 'direct_chat' 
                            ? !whatsappUserName.trim() 
                            : !whatsappPhoneNumber.trim()
                        )}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {whatsappLoading ? (
                        <div className="flex items-center space-x-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{settings?.whatsapp_chat_mode === 'direct_chat' ? 'Opening WhatsApp...' : 'Sending OTP...'}</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                          </svg>
                          <span>Start Chat</span>
                        </div>
                      )}
                      </button>
                      </>
                    ) : (
                      <>
                        <p className="text-green-700 dark:text-green-300 font-semibold mb-2">Please submit the OTP to start chatting.</p>
                        <div className="mb-4 max-w-xs mx-auto">
                          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-300 mb-2">
                            Enter OTP *
                          </label>
                          <input
                            type="text"
                            value={whatsappOtpCode}
                            onChange={(e) => {
                              setWhatsappOtpCode(e.target.value);
                              clearWhatsappError(); // Clear error when user starts typing
                            }}
                            placeholder="6-digit code"
                            maxLength="6"
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/95 dark:bg-gray-800/80 backdrop-blur-md transition-all duration-200 text-gray-900 dark:text-gray-100"
                          />
                        </div>
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={handleVerifyWhatsappOtp}
                            disabled={whatsappOtpVerifying || !whatsappOtpCode.trim()}
                            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {whatsappOtpVerifying ? (
                              <div className="flex items-center space-x-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Verifying...</span>
                              </div>
                            ) : (
                              <span>Verify OTP</span>
                            )}
                          </button>
                          <button
                            onClick={handleResendWhatsappOtp}
                            disabled={whatsappOtpResending}
                            className="px-4 py-3 rounded-full font-semibold border-2 border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {whatsappOtpResending ? 'Resending...' : 'Resend OTP'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {whatsappMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`message ${message.sender === 'user' ? 'user' : 'support'}`}
                      >
                        <div className="message-bubble">
                          <div className="flex items-start space-x-3">
                            {message.sender === 'support' && (
                              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                </svg>
                              </div>
                            )}
                            <div className="flex-1">
                          <div 
                            className="text-sm leading-relaxed whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{
                              __html: message.sender === 'ai' ? formatAiResponse(message.text) : message.text
                            }}
                          />
                              <div className="message-time">
                                {new Date(message.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                            {message.sender === 'user' && (
                              <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {whatsappLoading && (
                      <div className="message support">
                        <div className="typing-indicator">
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div ref={whatsappMessagesEndRef} />
              </div>

              {/* WhatsApp Chat Input */}
              {whatsappConnected && (
                <div className="chat-input">
                  <div className="flex items-center space-x-3">
                    <input
                      ref={whatsappInputRef}
                      type="text"
                      value={whatsappInputMessage}
                      onChange={(e) => setWhatsappInputMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendWhatsappMessage();
                        }
                      }}
                      placeholder="Type your message..."
                      className="flex-1"
                      disabled={whatsappLoading}
                    />
                    <button
                      onClick={sendWhatsappMessage}
                      disabled={whatsappLoading || !whatsappInputMessage.trim()}
                      className="send-button bg-gradient-to-r from-green-500 to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {whatsappLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default FloatingChatSystem;
